import { ref, computed } from 'vue'
import operationRecordAPI from '@/api/operationRecord'

// 操作记录的导入 / 导出
// 依赖：
//   selectedIds  - 表格勾选的记录 id 列表 (Ref)
//   showSnackbar - 提示回调 (text, color) => void
//   onImported   - 导入成功后的回调（用于刷新列表）
export function useRecordImportExport({ selectedIds, showSnackbar, onImported }) {
  const isExporting = ref(false)
  const isImporting = ref(false)
  const importFileInput = ref(null)
  const importDialog = ref(false)
  const importRecords = ref([])          // 从文件解析出的记录
  const importSelectedIndexes = ref([])  // 导入对话框中勾选的索引

  // 将记录归一化为可导出 / 可导入的结构
  // params 与 mcpData 后端以字符串存储，统一序列化为字符串
  const normalizeRecord = (item) => {
    const toStr = (v) => {
      if (v === null || v === undefined) return v
      return typeof v === 'string' ? v : JSON.stringify(v)
    }
    return {
      operationName: item.operationName ?? '',
      emoji: item.emoji ?? null,
      version: item.version ?? null,
      deviceId: item.deviceId ?? null,
      servoVersion: item.servoVersion ?? null,
      params: toStr(item.params),
      mcpData: toStr(item.mcpData),
      isFavorited: item.isFavorited ?? 0,
      createdAt: item.createdAt ?? null
    }
  }

  // 判断记录是否已包含完整的 params 与 mcpData
  const isRecordComplete = (item) =>
    item && item.params !== undefined && item.params !== null &&
    item.mcpData !== undefined && item.mcpData !== null

  // 根据 id 列表拉取完整记录（包含 params 与 mcpData）
  const fetchFullRecordsByIds = async (ids) => {
    const results = []
    for (const id of ids) {
      try {
        const res = await operationRecordAPI.getDetail(id)
        if (res && res.data) results.push(normalizeRecord(res.data))
      } catch (err) {
        console.error(`获取记录 ${id} 详情失败:`, err)
      }
    }
    return results
  }

  // 根据列表项补全记录：已完整的直接用，缺 mcpData 的再查详情
  const fetchFullRecordsByItems = async (items) => {
    const results = []
    for (const item of items) {
      if (isRecordComplete(item)) {
        results.push(normalizeRecord(item))
      } else {
        try {
          const res = await operationRecordAPI.getDetail(item.id)
          if (res && res.data) results.push(normalizeRecord(res.data))
        } catch (err) {
          console.error(`获取记录 ${item.id} 详情失败:`, err)
        }
      }
    }
    return results
  }

  // 触发文件下载
  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // 导出：scope = 'selected' | 'all'
  const exportRecords = async (scope) => {
    isExporting.value = true
    try {
      let records = []
      if (scope === 'selected') {
        const ids = [...selectedIds.value]
        if (ids.length === 0) {
          showSnackbar('请先勾选要导出的记录', 'warning')
          return
        }
        records = await fetchFullRecordsByIds(ids)
      } else {
        // 全量：拉取所有记录，必要时补全详情
        const res = await operationRecordAPI.getAll()
        const list = res?.data?.list || []
        if (list.length === 0) {
          showSnackbar('暂无可导出的记录', 'warning')
          return
        }
        records = await fetchFullRecordsByItems(list)
      }

      if (records.length === 0) {
        showSnackbar('导出失败：未获取到记录数据', 'error')
        return
      }

      const filename = `operation-records-${scope}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`
      downloadJson({
        type: 'operation-records',
        exportedAt: new Date().toISOString(),
        count: records.length,
        records
      }, filename)
      showSnackbar(`成功导出 ${records.length} 条记录`, 'success')
    } catch (err) {
      console.error('导出失败:', err)
      showSnackbar(`导出失败: ${err.message}`, 'error')
    } finally {
      isExporting.value = false
    }
  }

  // 点击导入按钮，打开文件选择器
  const triggerImport = () => {
    importFileInput.value && importFileInput.value.click()
  }

  // 解析所选文件
  const handleImportFileChange = (event) => {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result)
        // 兼容两种格式：导出对象 { records: [...] } 或直接数组 [...]
        const list = Array.isArray(parsed) ? parsed : (parsed.records || [])
        if (!Array.isArray(list) || list.length === 0) {
          showSnackbar('文件中没有可导入的操作记录', 'warning')
          return
        }
        importRecords.value = list.map(normalizeRecord)
        importSelectedIndexes.value = importRecords.value.map((_, idx) => idx)
        importDialog.value = true
      } catch (err) {
        console.error('解析文件失败:', err)
        showSnackbar('文件解析失败，请检查是否为有效的 JSON 文件', 'error')
      } finally {
        // 重置 input，便于再次选择同一文件
        event.target.value = ''
      }
    }
    reader.onerror = () => {
      showSnackbar('文件读取失败', 'error')
      event.target.value = ''
    }
    reader.readAsText(file)
  }

  // 确认导入：scope = 'selected' | 'all'
  const confirmImport = async (scope) => {
    const targets = scope === 'selected'
      ? importSelectedIndexes.value.map(idx => importRecords.value[idx])
      : [...importRecords.value]

    if (targets.length === 0) {
      showSnackbar('请先选择要导入的记录', 'warning')
      return
    }

    isImporting.value = true
    let success = 0
    let failed = 0
    try {
      for (const rec of targets) {
        // 导入时去除 id，由后端生成新记录，避免覆盖已有数据
        const payload = {
          operationName: rec.operationName,
          emoji: rec.emoji,
          version: rec.version,
          deviceId: rec.deviceId,
          servoVersion: rec.servoVersion,
          params: rec.params,
          mcpData: rec.mcpData
        }
        try {
          await operationRecordAPI.save(payload)
          success++
        } catch (err) {
          failed++
          console.error('导入单条记录失败:', err)
        }
      }
      if (failed === 0) {
        showSnackbar(`成功导入 ${success} 条记录`, 'success')
      } else {
        showSnackbar(`导入完成：成功 ${success} 条，失败 ${failed} 条`, 'warning')
      }
      importDialog.value = false
      if (onImported) await onImported()
    } catch (err) {
      console.error('导入失败:', err)
      showSnackbar(`导入失败: ${err.message}`, 'error')
    } finally {
      isImporting.value = false
    }
  }

  return {
    isExporting, isImporting,
    importFileInput, importDialog, importRecords, importSelectedIndexes,
    exportRecords, triggerImport, handleImportFileChange, confirmImport
  }
}
